import React, {useEffect, useState, useRef, useMemo} from "react";
import {useNavigate} from "react-router-dom";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Grid,
    Box,
    Divider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    Chip,
    Card,
    CardContent,
    Avatar,
    Paper,
    DialogContentText, Container,
} from "@mui/material";
import CakeIcon from "@mui/icons-material/Cake";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FlagIcon from "@mui/icons-material/Flag";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import PersonIcon from "@mui/icons-material/Person";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import HistoryIcon from "@mui/icons-material/History";
import LeaderboardModal from "./LeaderboardModal";
import ProfileModal from "./ProfileModal";
import SendMessageModal from './SendMessageModal';
import SendIcon from '@mui/icons-material/Send';
import DOMPurify from 'dompurify'; // You'll need to install this package
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AddAttendeeModal from "./AddAttendeeModal";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents"; // 🏆 Trophy icon for leaderboard
import HowToRegIcon from "@mui/icons-material/HowToReg";
import Link from '@mui/material/Link';
import Checkbox from '@mui/material/Checkbox';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AffiliateTermsModal from './affiliateTermsModal';
import {acceptAffiliateTerms, isUserAcceptedAffiliateTerms, getAffiliateById} from '../api/affiliateApi';
import GuestRegistrationsList from './GuestRegistrationsList';

// Import new modular components
import WorkoutInfo from './class-modal/WorkoutInfo';
import ClassDetails from './class-modal/ClassDetails';
import ParticipantsList from './class-modal/ParticipantsList';
import WaitlistSection from './class-modal/WaitlistSection';
import ScoreForm from './class-modal/ScoreForm';
import ConfirmDialogs from './class-modal/ConfirmDialogs';
import WorkoutInfoFullScreen from './class-modal/WorkoutInfoFullScreen';
import SmartLockUnlockButton from "./SmartLockUnlockButton";

// Import custom hooks and utilities
import { useClassData } from '../hooks/useClassData';
import { 
    isClassTimePassed as checkClassTimePassed, 
    isClassOver as checkClassOver,
    getFlagColor,
    allAttendeesCheckedIn,
    formatClassDate,
    formatClassTimeOnly,
    isActiveAffiliatePlan,
    canSeeParticipantsWithPlan
} from '../utils/classModalHelpers';

// Import API functions
import {
    getClassAttendees,
    checkInAttendee,
    deleteAttendee,
    getGuestRegistrations,
    registerGuestForClass,
    deleteGuestRegistration,
} from "../api/classesApi";
import {
    getUserClassScore,
    addClassScore,
    updateClassScore,
    registerForClass,
    cancelRegistration,
    checkUserEnrollment,
    getWaitlist,
    createWaitlist,
    deleteWaitlist,
    addClassToMyTrainings,
    promoteFromWaitlist,
    getClassCancellationHistory,
    getClassWorkoutImage
} from "../api/classesApi";
import {getUserPlansByAffiliate, getUserProfile} from "../api/profileApi";
import {getMemberInfo} from "../api/membersApi";
import {getFamilyMembers} from "../api/familyApi";
import {getAppliedWorkYourWeakness} from "../api/capApi";
import {getWeekCompeteDays} from "../api/wodApi";
import * as PropTypes from "prop-types";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CircularProgress from "@mui/material/CircularProgress"; // Import the family API function


export default function ClassModal({
                                       open,
                                       onClose,
                                       onExited,
                                       onRegistrationSuccess,
                                       cls,
                                       onEdit,
                                       onDelete,
                                       refreshClasses,
                                       affiliateId,
                                       affiliateEmail,
                                       trainers = [],
                                       props,
                                       registrationPeriod,
                                       selectedAffiliateListVisible,
                                       isAllClassListVisible,
                                       workoutInfoVisibilityMode = "always",
                                       hideTrainer = false,
                                       cancelBeforeHours,
                                       refreshKey = 0
                                   }) {
    const navigate = useNavigate();
    const [isLeaderboardOpen, setLeaderboardOpen] = useState(false);
    const [userRole, setUserRole] = useState(() => localStorage.getItem("role")); // Initialize from localStorage immediately
    const [affiliateSubdomain, setAffiliateSubdomain] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isProfileOpen, setProfileOpen] = useState(false);
    const [isCheckingInAll, setIsCheckingInAll] = useState(false);
    // State for full-screen workout info
    const [isWorkoutInfoFullScreen, setWorkoutInfoFullScreen] = useState(false);
    const [publicWorkYourWeaknessContent, setPublicWorkYourWeaknessContent] = useState("");
    const [classWorkoutImage, setClassWorkoutImage] = useState(null);
    const [competeDayFallbackContent, setCompeteDayFallbackContent] = useState(null);

    const currentUserId = useMemo(() => {
        try {
            const token = localStorage.getItem("token");
            if (!token) return null;
            const payload = JSON.parse(atob(token.split(".")[1]));
            const id = parseInt(payload?.userId, 10);
            return Number.isInteger(id) && id > 0 ? id : null;
        } catch (error) {
            return null;
        }
    }, [open]);

    const canManageClass = userRole === "affiliate" || userRole === "trainer";
    const isTemporaryTrainer = useMemo(() => {
        if (!cls?.temporaryTrainerId || !currentUserId || canManageClass) {
            return false;
        }
        return parseInt(cls.temporaryTrainerId, 10) === currentUserId;
    }, [cls?.temporaryTrainerId, currentUserId, canManageClass]);
    
    // Registration and waitlist states
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [compatiblePlans, setCompatiblePlans] = useState([]);
    const [hasAnyPlans, setHasAnyPlans] = useState(false);
    const [canSeeParticipantList, setCanSeeParticipantList] = useState(false);
    const [hasCompatiblePlans, setHasCompatiblePlans] = useState(false);

    // Use custom hook for class data management
    const {
        attendees,
        guestRegistrations,
        waitlistEntries,
        waitlistCount,
        isLoading: isLoadingClassData,
        fetchAttendees,
        fetchGuestRegistrations,
        fetchWaitlistEntries,
        fetchWaitlistCount,
        refreshAllData,
        setAttendees,
        setGuestRegistrations,
        setWaitlistCount
    } = useClassData(
        cls,
        open,
        userRole,
        selectedAffiliateListVisible,
        isAllClassListVisible,
        isTemporaryTrainer,
        canSeeParticipantList
    );

    // Family member states
    const [familyMembers, setFamilyMembers] = useState([]);
    const [selectedBookFor, setSelectedBookFor] = useState("self"); // "self" or familyMember.id
    const [userProfile, setUserProfile] = useState(null);
    const [filteredPlans, setFilteredPlans] = useState([]);

    const [showScoreForm, setShowScoreForm] = useState(false);
    const [scoreType, setScoreType] = useState("rx");  // rx | sc | beg
    const [scoreValue, setScoreValue] = useState("");
    const [commentValue, setCommentValue] = useState("");
    const [hasScore, setHasScore] = useState(false);

    // Aitame tuvastada, kas kasutaja on *juba* registreerunud sellesse klassi
    const [isRegistered, setIsRegistered] = useState(false);
    const [isFirstTraining, setIsFirstTraining] = useState(false);
    // Uued waitlisti jaoks vajalikud state'id
    const [isInWaitlist, setIsInWaitlist] = useState(false);
    const [isClassFull, setIsClassFull] = useState(false);

    const [waitlistStatus, setWaitlistStatus] = useState({
        userInWaitlist: false,
        familyMemberWaitlists: [] // Array of family member waitlist entries
    });

    const [isSendMessageOpen, setSendMessageOpen] = useState(false);

    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [waitlistRemoveConfirmOpen, setWaitlistRemoveConfirmOpen] = useState(false);
    const [deleteAttendeeConfirmOpen, setDeleteAttendeeConfirmOpen] = useState(false);
    const [attendeeToDelete, setAttendeeToDelete] = useState(null);
    const [deleteClassConfirmOpen, setDeleteClassConfirmOpen] = useState(false);
    const [isAddAttendeeModalOpen, setAddAttendeeModalOpen] = useState(false);


    const [notificationOpen, setNotificationOpen] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");
    const [notificationType, setNotificationType] = useState("success");

    const profileFetchedRef = useRef(false);
    const userProfileRef = useRef(null);
    const affiliateDataFetchedRef = useRef(null); // Track last fetched affiliate ID

    const [termsModalOpen, setTermsModalOpen] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);
    const [termsCheck, setTermsCheck] = useState(false);

    const [isLoadingUserData, setIsLoadingUserData] = useState(false); // Track if user data is being loaded

    const [familyMemberEnrollments, setFamilyMemberEnrollments] = useState([]);
    const [selectedFamilyMemberToCancel, setSelectedFamilyMemberToCancel] = useState(null);
    
    // Loading states for delete and promote actions
    const [isDeletingAttendee, setIsDeletingAttendee] = useState(false);
    const [isDeletingClass, setIsDeletingClass] = useState(false);
    const [isPromotingFromWaitlist, setIsPromotingFromWaitlist] = useState(null); // Store waitlistId being promoted
    const [familyCancelConfirmOpen, setFamilyCancelConfirmOpen] = useState(false);
    const [isWithinPeriod, setIsWithinPeriod] = useState(true);

    const [userWaitlistPosition, setUserWaitlistPosition] = useState(null);
    const [familyWaitlistPositions, setFamilyWaitlistPositions] = useState({});

    const [isRegistering, setIsRegistering] = useState(false);
    const [additionalInfo, setAdditionalInfo] = useState("");
    const [isCancelling, setIsCancelling] = useState(false);
    const [cancellationHistoryOpen, setCancellationHistoryOpen] = useState(false);
    const [isLoadingCancellationHistory, setIsLoadingCancellationHistory] = useState(false);
    const [cancellationHistory, setCancellationHistory] = useState([]);
    const [cancellationHistoryError, setCancellationHistoryError] = useState("");
    const [isAddingToWaitlist, setIsAddingToWaitlist] = useState(false);
    const [isRemovingFromWaitlist, setIsRemovingFromWaitlist] = useState(false);
    const smartLockAffiliateId = affiliateId || cls?.affiliateId || null;
    const canShowSmartLockForRole = userRole !== "affiliate" && userRole !== "trainer";

    const loadCancellationHistory = async () => {
        if (!cls?.id || (userRole !== "affiliate" && userRole !== "trainer")) {
            setCancellationHistory([]);
            setCancellationHistoryError("");
            return;
        }

        try {
            setIsLoadingCancellationHistory(true);
            setCancellationHistoryError("");
            const historyData = await getClassCancellationHistory(cls.id);
            setCancellationHistory(Array.isArray(historyData) ? historyData : []);
        } catch (error) {
            console.error("Error loading cancellation history:", error);
            setCancellationHistory([]);
            setCancellationHistoryError("Failed to load cancellation history");
        } finally {
            setIsLoadingCancellationHistory(false);
        }
    };

    useEffect(() => {
        if (open && cancellationHistoryOpen && (userRole === "affiliate" || userRole === "trainer") && cls?.id) {
            loadCancellationHistory();
        } else if (!open) {
            setCancellationHistory([]);
            setCancellationHistoryError("");
            setCancellationHistoryOpen(false);
        }
    }, [open, cancellationHistoryOpen, userRole, cls?.id]);

    // Fetch affiliate subdomain for registration link generation
    useEffect(() => {
        const fetchAffiliateData = async () => {
            // Only fetch if:
            // - modal is open
            // - affiliateId exists
            // - user is affiliate or trainer
            // - we haven't fetched this affiliate's data yet
            if (open && affiliateId && (userRole === "affiliate" || userRole === "trainer") && affiliateDataFetchedRef.current !== affiliateId) {
                try {
                    const affiliateData = await getAffiliateById(affiliateId);
                    if (affiliateData?.subdomain) {
                        setAffiliateSubdomain(affiliateData.subdomain);
                        affiliateDataFetchedRef.current = affiliateId; // Mark as fetched
                    }
                } catch (error) {
                    console.error("Error fetching affiliate subdomain:", error);
                }
            }
        };

        fetchAffiliateData();
    }, [open, affiliateId, userRole]);

    useEffect(() => {
        const loadPublicWorkYourWeakness = async () => {
            if (!open || !affiliateId || !cls?.time) {
                setPublicWorkYourWeaknessContent("");
                return;
            }

            try {
                const classDate = new Date(cls.time);
                const day = classDate.getDay();
                const diff = classDate.getDate() - day + (day === 0 ? -6 : 1);
                const weekStart = new Date(classDate.setDate(diff));
                weekStart.setHours(0, 0, 0, 0);

                const data = await getAppliedWorkYourWeakness({
                    affiliateId,
                    weekStart: weekStart.toISOString()
                });

                if (data?.isPublic && data?.content) {
                    setPublicWorkYourWeaknessContent(data.content);
                } else {
                    setPublicWorkYourWeaknessContent("");
                }
            } catch (error) {
                setPublicWorkYourWeaknessContent("");
            }
        };

        loadPublicWorkYourWeakness();
    }, [open, affiliateId, cls?.time]);

    useEffect(() => {
        let isCancelled = false;

        const loadClassWorkoutImage = async () => {
            if (!open || !cls?.id) {
                setClassWorkoutImage(null);
                return;
            }

            const imageData = await getClassWorkoutImage(cls.id);
            if (!isCancelled) {
                setClassWorkoutImage(imageData);
            }
        };

        loadClassWorkoutImage();
        return () => {
            isCancelled = true;
        };
    }, [open, cls?.id, refreshKey]);

    useEffect(() => {
        const localDateKey = (d) => {
            const x = new Date(d);
            if (Number.isNaN(x.getTime())) return "";
            return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
        };
        const getStartOfWeekMonday = (baseDate) => {
            const date = new Date(baseDate);
            date.setDate(date.getDate() - (date.getDay() || 7) + 1);
            date.setHours(0, 0, 0, 0);
            return date;
        };

        const affiliateIdForCompete = cls?.affiliateId ?? affiliateId;
        if (!open || !affiliateIdForCompete || !cls?.time) {
            setCompeteDayFallbackContent(null);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const classTime = new Date(cls.time);
                const weekStart = getStartOfWeekMonday(classTime);
                const days = await getWeekCompeteDays(affiliateIdForCompete, weekStart);
                if (cancelled) return;
                if (!Array.isArray(days)) {
                    setCompeteDayFallbackContent(null);
                    return;
                }
                const dayKey = localDateKey(classTime);
                const entry = days.find((d) => localDateKey(d.date) === dayKey);
                const content = entry?.content && String(entry.content).trim() ? entry.content : null;
                if (!cancelled) setCompeteDayFallbackContent(content);
            } catch {
                if (!cancelled) setCompeteDayFallbackContent(null);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, cls?.id, cls?.affiliateId, cls?.time, affiliateId, refreshKey]);

    // PEAMINE USEEFFECT - laeb kõik vajalikud andmed ühes kohas
    useEffect(() => {
        if (open && userRole === "regular") {
            // Always show loading when opening modal for regular users
            setIsLoadingUserData(true);

            if (!profileFetchedRef.current) {
                const fetchUserData = async () => {
                    try {
                        profileFetchedRef.current = true;

                        // Lae profiil ainult üks kord ja salvesta ref'i
                        const profile = await getUserProfile();
                        setUserProfile(profile);
                        userProfileRef.current = profile; // Cache profiil

                        if (cls?.affiliateId) {
                            // Lae pereliikmed
                            const members = await getFamilyMembers();
                            setFamilyMembers(members || []);

                            // Lae kasutaja plaanid
                            await loadUserPlans(cls.affiliateId);
                        }


                        if (cls?.id) {
                            await checkIfCurrentUserIsRegistered();
                            await calculateWaitlistPositions(); // Also updates waitlist count and isInWaitlist
                        }

                    } catch (error) {
                        console.error("Error loading user data:", error);
                    } finally {
                        setIsLoadingUserData(false);
                    }
                };

                fetchUserData();
            } else {
                // Profile already fetched, just refresh enrollment status
                const refreshEnrollmentStatus = async () => {
                    try {
                        if (cls?.id) {
                            await checkIfCurrentUserIsRegistered();
                            await calculateWaitlistPositions();
                        }
                    } catch (error) {
                        console.error("Error refreshing enrollment status:", error);
                    } finally {
                        setIsLoadingUserData(false);
                    }
                };

                refreshEnrollmentStatus();
            }
        } else if (!open) {
            profileFetchedRef.current = false;
            userProfileRef.current = null;
            affiliateDataFetchedRef.current = null; // Reset affiliate data cache
            setIsLoadingUserData(false); // Reset loading state

        } else {

        }
    }, [open, userRole, cls]);
    // Toggle workout info full-screen
    const toggleWorkoutInfoFullScreen = () => {
        setWorkoutInfoFullScreen(!isWorkoutInfoFullScreen);
    };
    // Sulge täisekraan (nt Compete modali avamisel, et ei näidataks RX kava taga)
    const closeWorkoutInfoFullScreen = () => {
        setWorkoutInfoFullScreen(false);
    };

    // Kontrollime, kas klass on täis - use dynamic count from attendees and guests
    useEffect(() => {
        if (cls && typeof cls.memberCapacity !== 'undefined') {
            // Use live count if data was fetched (affiliate, trainer, or viewing specific lists)
            // Otherwise use cls.enrolledCount from server
            const shouldUseLiveCount = 
                userRole === "affiliate" || 
                userRole === "trainer" ||
                isTemporaryTrainer ||
                selectedAffiliateListVisible === true || 
                (isAllClassListVisible === true && canSeeParticipantList === true);
            
            const currentEnrolledCount = shouldUseLiveCount
                ? (attendees?.length || 0) + (guestRegistrations?.length || 0)
                : (cls.enrolledCount || 0);
            setIsClassFull(currentEnrolledCount >= cls.memberCapacity);
        }
    }, [cls, attendees, guestRegistrations, userRole, isTemporaryTrainer, selectedAffiliateListVisible, isAllClassListVisible, canSeeParticipantList]);

    useEffect(() => {
        // Kui modal suletakse (open muutub false'iks), lähtesta state'd
        if (!open) {
            setShowScoreForm(false);
            setSelectedBookFor("self"); // Reset to default
        }
    }, [open]);


    // Initialize and auto-select first available option in "Book For" dropdown
    useEffect(() => {
        if (!open) return; // Only run when modal is open

        // Check if current selectedBookFor is still valid
        const isSelfAvailable = !isRegistered;
        const availableFamilyMembers = familyMembers.filter(member => 
            !familyMemberEnrollments.some(enrollment => enrollment.familyMemberId === member.id)
        );

        // Always set to first available option when modal opens or registration status changes
        if (isSelfAvailable) {
            // Self is available, select it if not already selected
            if (selectedBookFor !== "self") {
                setSelectedBookFor("self");
            }
        } else if (availableFamilyMembers.length > 0) {
            // Self not available, select first available family member
            const firstAvailableMemberId = availableFamilyMembers[0].id.toString();
            if (selectedBookFor !== firstAvailableMemberId) {
                setSelectedBookFor(firstAvailableMemberId);
            }
        }
    }, [open, isRegistered, familyMembers, familyMemberEnrollments]);

    // Update filtered plans when selectedBookFor changes
    useEffect(() => {
        filterCompatiblePlans();
    }, [selectedBookFor, compatiblePlans]);

    useEffect(() => {
        if (cls && registrationPeriod) {
            const now = new Date();
            const classDate = new Date(cls.time);

            const periodInDays = registrationPeriod * 7;
            const registrationEndDate = new Date();
            registrationEndDate.setDate(now.getDate() + periodInDays);

            const withinPeriod = classDate <= registrationEndDate;
            setIsWithinPeriod(withinPeriod);
        } else {
            setIsWithinPeriod(true);
        }
    }, [cls, registrationPeriod]);

    // Calculate user and family member positions in waitlist
    // Also updates waitlist count and isInWaitlist status to avoid duplicate API calls
    async function calculateWaitlistPositions() {
        try {
            if (!cls || !cls.id) return;

            const waitlist = await getWaitlist(cls.id);
            
            // Update waitlist count (avoids separate fetchWaitlistCount call)
            setWaitlistCount(waitlist ? waitlist.length : 0);

            // Arvuta kasutaja positsioon waitlistis
            if (waitlist && waitlist.length > 0 && userProfileRef.current) {
                const userId = userProfileRef.current.id;

                // Check if user is in waitlist (avoids separate check)
                const userInWaitlist = waitlist.some(item => {
                    const itemUserId = typeof item.userId === 'string' ? parseInt(item.userId) : item.userId;
                    return itemUserId === userId;
                });
                setIsInWaitlist(userInWaitlist);

                // Leia kasutaja positsioon (index + 1, sest positsioon algab 1-st)
                const userIndex = waitlist.findIndex(entry => {
                    const entryUserId = typeof entry.userId === 'string' ? parseInt(entry.userId) : entry.userId;
                    return entryUserId === userId && !entry.familyMember;
                });

                if (userIndex !== -1) {
                    setUserWaitlistPosition(userIndex + 1);
                } else {
                    setUserWaitlistPosition(null);
                }

                // Leia pereliikmed waitlistis ja nende positsioonid
                const familyPositions = {};
                waitlist.forEach((entry, index) => {
                    if (entry.familyMember && entry.registrantId === userId) {
                        familyPositions[entry.familyMember.id] = index + 1;
                    }
                });
                setFamilyWaitlistPositions(familyPositions);
            } else {
                setIsInWaitlist(false);
                setUserWaitlistPosition(null);
                setFamilyWaitlistPositions({});
            }
        } catch (error) {
            console.error("Error calculating waitlist positions:", error);
            setIsInWaitlist(false);
            setUserWaitlistPosition(null);
            setFamilyWaitlistPositions({});
            setWaitlistCount(0);
        }
    }

    // Update the filterCompatiblePlans function to exclude already registered family members
    const filterCompatiblePlans = () => {
        if (!compatiblePlans || compatiblePlans.length === 0) {
            setFilteredPlans([]);
            return;
        }

        // Get list of registered family member IDs
        const registeredFamilyMemberIds = familyMemberEnrollments.map(enrollment => enrollment.familyMemberId);

        // Filter based on the selected book-for person
        const filtered = compatiblePlans.filter(plan => {
            // If "self" is selected, show plans without a familyMemberId (only if user is not registered)
            if (selectedBookFor === "self") {
                return !plan.familyMemberId && !isRegistered;
            }
            // Otherwise show plans for the selected family member (only if they're not already registered)
            else {
                const familyMemberId = parseInt(selectedBookFor);
                return plan.familyMemberId === familyMemberId &&
                    !registeredFamilyMemberIds.includes(familyMemberId);
            }
        });

        setFilteredPlans(filtered);
        setHasCompatiblePlans(filtered.length > 0);

        // Set the first filtered plan as default selection if available
        if (filtered.length > 0) {
            setSelectedPlanId(filtered[0].id);
        } else {
            setSelectedPlanId(null);
        }
    };

    // Add function to handle family member cancellation
    const handleCancelFamilyMemberRegistration = async (familyMemberEnrollmentId) => {
        try {
            setIsCancelling(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/classes/cancel-family-member`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    enrollmentId: familyMemberEnrollmentId,
                    freeClass: cls.freeClass
                })
            });

            if (!response.ok) {
                throw new Error('Failed to cancel family member registration');
            }

            // Refresh enrollment data
            await checkIfCurrentUserIsRegistered();
            await refreshClasses();
            showNotification("Family member registration cancelled successfully!", "success");
        } catch (error) {
            console.error("Error cancelling family member registration:", error);
            showNotification("Failed to cancel family member registration", "error");
        } finally {
            setIsCancelling(false);
        }
    };

    const handlePromoteFromWaitlist = async (waitlistId) => {
        setIsPromotingFromWaitlist(waitlistId);
        try {
            await promoteFromWaitlist(waitlistId);
            await fetchWaitlistEntries();
            await fetchAttendees();
            await refreshClasses();
            showNotification("User promoted from waitlist successfully!", "success");
        } catch (error) {
            console.error("Error promoting from waitlist:", error);
            showNotification("Failed to promote user from waitlist", "error");
        } finally {
            setIsPromotingFromWaitlist(null);
        }
    };

    // Note: fetchWaitlistEntries is now handled by useClassData hook


    // SALVESTA / UUENDA SKOOR

    async function handleSaveScore() {
        try {
            if (!scoreValue || !scoreType) {
                showNotification("Please fill the score and scoreType!", "info");
                return;
            }

            // Kontrolli, kas scoreValue sisaldab ainult lubatud märke
            const allowedPattern = /^[0-9.,:]+$/;
            if (!allowedPattern.test(scoreValue)) {
                showNotification("Score can only contain numbers, comma (,), period (.) and colon (:)", "error");
                return;
            }

            if (!cls || !cls.id) {
                console.error("No classId found!");
                return;
            }

            // Kas loome uue kirje või uuendame?
            if (hasScore) {
                // Uuenda
                await updateClassScore(cls, scoreType, scoreValue, commentValue);
            } else {
                // Lisa uus
                await addClassScore(cls, scoreType, scoreValue, commentValue);
            }

            // Salvesta vastav kava treeningpäevikusse (RX -> rx, Scaled -> intermediate, BEG -> beginner)
            // Leaderboardist ei lisa compete osa; compete läheb ainult Compete modali "Add to trainings" kaudu
            const scoreTypeToTrack = { rx: 'rx', sc: 'intermediate', beg: 'beginner' };
            const selectedTrack = scoreTypeToTrack[scoreType] || 'rx';
            await addClassToMyTrainings(cls.id, false, selectedTrack);

            showNotification("Score saved successfully!", "success");
            setShowScoreForm(false);
            setHasScore(true);

        } catch (error) {
            console.error("Error saving score:", error);
            showNotification("Failed to save score", "error");
        }
    }

    const showNotification = (message, type = "info") => {
        setNotificationMessage(message);
        setNotificationType(type);
        setNotificationOpen(true);
    };

    const handleBuyPlans = () => {
        navigate("/register-training", {
            state: {
                affiliateId: cls.affiliateId,
                openPlans: true
            }
        });
    };

    // Filter plans based on expiration, training type and available sessions
    async function loadUserPlans(affiliateId) {
        try {
            const [affiliatePlans, classPlans] = await Promise.all([
                getUserPlansByAffiliate(affiliateId, null, true),
                cls?.id
                    ? getUserPlansByAffiliate(affiliateId, cls.id)
                    : Promise.resolve([])
            ]);

            // Any active package at this club (not limited to this class training type)
            const plans = Array.isArray(affiliatePlans) ? affiliatePlans : [];
            const hasActiveAffiliatePlan = plans.some((plan) => isActiveAffiliatePlan(plan));
            const hasParticipantVisibilityPlan = plans.some((plan) =>
                canSeeParticipantsWithPlan(plan, cls?.time)
            );

            setHasAnyPlans(hasActiveAffiliatePlan);
            setCanSeeParticipantList(hasParticipantVisibilityPlan);
            // Backend already applies valid-plan rules (date/training type/sessions/schedule) for classId.
            setCompatiblePlans(Array.isArray(classPlans) ? classPlans : []);

            // Initial filtering will happen in the useEffect when selectedBookFor changes
        } catch (error) {
            console.error("Error loading user plans:", error);
            setCompatiblePlans([]);
            setHasAnyPlans(false);
            setCanSeeParticipantList(false);
            setHasCompatiblePlans(false);
        }
    }

    async function handleAddClassToMyTrainings(selectedTrack = 'rx', addCompetition = false) {
        try {
            if (!cls || !cls.id) return;
            await addClassToMyTrainings(cls.id, addCompetition, selectedTrack);
            showNotification("Class added to your trainings!", "success");
        } catch (error) {
            console.error("Error adding class to my trainings:", error);
            showNotification("Failed to add class to my trainings", "error");
        }
    }

    // Note: fetchAttendees, fetchGuestRegistrations are now handled by useClassData hook

    // Handle add guest registration
    const handleAddGuest = async (guestData) => {
        try {
            await registerGuestForClass(
                guestData.classId,
                guestData.firstName,
                guestData.lastName,
                guestData.email,
                guestData.affiliateId,
                guestData.additionalInfo || ''
            );
            
            // Refresh guest registrations and class data
            await fetchGuestRegistrations();
            await refreshClasses();
            showNotification("Guest added successfully!", "success");
        } catch (error) {
            console.error("Error adding guest:", error);
            throw error; // Propagate error to component
        }
    };

    // Handle delete guest registration
    const handleDeleteGuest = async (guestId) => {
        try {
            await deleteGuestRegistration(guestId);
            
            // Refresh guest registrations and class data
            await fetchGuestRegistrations();
            await refreshClasses();
            showNotification("Guest removed successfully!", "success");
        } catch (error) {
            console.error("Error deleting guest:", error);
            showNotification("Failed to delete guest", "error");
        }
    };

    // ClassModal.js - uuenda checkIfCurrentUserIsRegistered
    async function checkIfCurrentUserIsRegistered() {
        try {
            const response = await checkUserEnrollment(cls.id);


            // Set registration status
            setIsRegistered(response.enrolled);
            setIsFirstTraining(response.firstTraining);
            setTermsAccepted(response.accepted);
            setFamilyMemberEnrollments(response.familyMemberEnrollments || []);

            // LISA: Set waitlist status
            setWaitlistStatus(response.waitlistStatus || {
                userInWaitlist: false,
                familyMemberWaitlists: []
            });

            // Update legacy isInWaitlist for backward compatibility
            setIsInWaitlist(response.waitlistStatus?.userInWaitlist ||
                (response.waitlistStatus?.familyMemberWaitlists?.length > 0));

            // Score handling
            if (response.hasScore) {
                setHasScore(true);
                setScoreType(response.scoreType || "rx");
                setScoreValue(response.score || "");
                setCommentValue(response.comment || "");
            } else {
                setHasScore(false);
                setScoreType("rx");
                setScoreValue("");
                setCommentValue("");
            }
        } catch (error) {
            console.error("Error checking enrollment:", error);
            setFamilyMemberEnrollments([]);
            setWaitlistStatus({
                userInWaitlist: false,
                familyMemberWaitlists: []
            });
        }
    }

    // Use utility function from helpers
    const isClassTimePassed = () => checkClassTimePassed(cls?.time);

    const handleRegister = async () => {
        try {
            setIsRegistering(true);

            const isFreeOrFirstTraining = cls.freeClass || isFirstTraining;

            if (!isFreeOrFirstTraining) {
                if (!selectedPlanId) {
                    showNotification("Please select a plan first!", "info");
                    return;
                }

                // Check if selected plan is in filtered plans
                const selectedPlan = filteredPlans.find(plan => plan.id === selectedPlanId);
                if (!selectedPlan) {
                    showNotification("Please select a valid plan for this class type!", "info");
                    return;
                }
            }

            // Handle terms acceptance at registration time
            if (termsCheck && !termsAccepted) {
                setIsAcceptingTerms(true);
                try {
                    await acceptAffiliateTerms({
                        affiliateId: cls.affiliateId
                    });
                    setTermsAccepted(true);
                } catch (error) {
                    console.error("Failed to accept terms:", error);
                    showNotification("Failed to accept terms", "error");
                    setIsAcceptingTerms(false);
                    return;
                }
                setIsAcceptingTerms(false);
            }

            // Determine if booking for a family member
            const isFamilyMember = selectedBookFor !== "self";
            const familyMemberId = isFamilyMember ? parseInt(selectedBookFor) : null;

            // Call register API with family member details
            const registrationResult = await registerForClass(
                cls.id,
                selectedPlanId,
                cls.affiliateId,
                cls.freeClass,
                isFamilyMember,
                familyMemberId,
                additionalInfo
            );

            // Close the class dialog first. The parent refreshes classes after
            // the dialog exit transition, avoiding nested dialogs and
            // background scrolling on affected Android browsers.
            if (onRegistrationSuccess) {
                await onRegistrationSuccess(registrationResult);
            } else {
                if (registrationResult?.calendarMessage) {
                    showNotification(registrationResult.calendarMessage, "warning");
                }
                onClose();
            }
        } catch (error) {
            console.error("Error registering for class:", error);
            const serverErrorMessage = error?.response?.data?.details || error?.response?.data?.error;
            const baseErrorMessage = serverErrorMessage || error.message || "Registration failed";
            const supportMessage = "IMPORTANT: Please refresh your app or contact your affiliate.";
            showNotification(`${baseErrorMessage}\n\n${supportMessage}`, "error");
        } finally {
            setIsRegistering(false);
        }
    };


    // Lisa see funktsioon handleCheckIn funktsiooni järgi (umbes real 705):
    const handleCheckInAll = async () => {
        if (attendees.length === 0) return;

        setIsCheckingInAll(true);
        try {
            // Get all attendees who are not checked in
            const uncheckedAttendees = attendees.filter(attendee => !attendee.checkIn);

            // Check in all unchecked attendees
            const checkInPromises = uncheckedAttendees.map(attendee => {
                const userId = attendee.userId;
                const familyMemberId = attendee.isFamilyMember ?
                    (attendee.familyMemberId || attendee.familyMember?.id) : null;

                return checkInAttendee(cls.id, userId, familyMemberId); // ✅ familyMemberId kaasa
            });

            await Promise.all(checkInPromises);

            // Update local state
            setAttendees(prev =>
                prev.map(attendee => ({...attendee, checkIn: true}))
            );

            await refreshClasses();
            showNotification("All attendees checked in successfully!", "success");
        } catch (error) {
            console.error("Error checking in all attendees:", error);
            showNotification("Failed to check in all attendees", "error");
        } finally {
            setIsCheckingInAll(false);
        }
    };

    // Lisa see handleCheckInAll funktsiooni järgi:
    // Use utility function from helpers
    const allCheckedIn = allAttendeesCheckedIn(attendees);

    // Tühista registreerimise funktsioon
    const handleCancelRegistration = async (familyMemberId = null) => {
        try {
            setIsCancelling(true);
            await cancelRegistration(cls.id, cls.freeClass, familyMemberId);
            await fetchAttendees();
            await checkIfCurrentUserIsRegistered()


            if (userRole === "affiliate" || userRole === "trainer") {
                // Refresh waitlist after cancellation to reflect changes
                await fetchWaitlistEntries();
            }
            setIsRegistered(false)
            await refreshClasses();

            if (cls?.affiliateId) {
                await loadUserPlans(cls.affiliateId);
            }

        } catch (error) {
            console.error("Error canceling registration:", error);
            showNotification(error.message || "Cancellation failed", "error");
        } finally {
            setIsCancelling(false);

        }
    };

    // UPDATED: Add to waitlist with family member support
    const handleAddToWaitlist = async () => {
        try {
            setIsAddingToWaitlist(true);

            // Require plan selection for paid classes
            if (!cls.freeClass && !isFirstTraining) {
                if (!selectedPlanId) {
                    showNotification("Please select a plan first!", "info");
                    return;
                }

                // Verify that selected plan is in filtered plans
                const selectedPlan = filteredPlans.find(plan => plan.id === selectedPlanId);
                if (!selectedPlan) {
                    showNotification("Selected plan is not compatible with this class type!", "info");
                    return;
                }
            }

            // Determine if booking for a family member
            const isFamilyMember = selectedBookFor !== "self";
            const familyMemberId = isFamilyMember ? parseInt(selectedBookFor) : null;

            // Check if this person is already in waitlist
            if (isPersonInWaitlist(selectedBookFor)) {
                const personName = isFamilyMember ?
                    familyMembers.find(m => m.id === familyMemberId)?.fullName || "Family member" :
                    "You";
                showNotification(`${personName} ${isFamilyMember ? 'is' : 'are'} already on the waitlist!`, "info");
                return;
            }

            // Call waitlist API with family member details
            await createWaitlist(
                cls.id,
                selectedPlanId,
                isFamilyMember,
                familyMemberId
            );

            // Refresh enrollment and waitlist status
            await checkIfCurrentUserIsRegistered();
            await calculateWaitlistPositions(); // Also updates waitlist count
            await refreshClasses();
            const personName = isFamilyMember ?
                familyMembers.find(m => m.id === familyMemberId)?.fullName || "Family member" :
                "You";
            showNotification(`${personName} ${isFamilyMember ? 'has' : 'have'} been added to the waitlist!`, "success");
        } catch (error) {
            console.error("Error adding to waitlist:", error);
            showNotification(error.response?.data?.error || error.message || "Failed to add to waitlist", "error");
        } finally {
            setIsAddingToWaitlist(false);
        }
    };

    // UPDATED: Remove from waitlist with family member support
    const handleRemoveFromWaitlist = async (type, familyMemberId = null) => {
        try {

            setIsRemovingFromWaitlist(true);

            const isFamilyMember = type === "family";

            await deleteWaitlist(cls.id, isFamilyMember, familyMemberId);

            // Refresh enrollment and waitlist status
            await checkIfCurrentUserIsRegistered();
            await calculateWaitlistPositions(); // Also updates waitlist count
            await refreshClasses();
            const personName = isFamilyMember ?
                familyMembers.find(m => m.id === familyMemberId)?.fullName || "Family member" :
                "You";
            showNotification(`${personName} ${isFamilyMember ? 'has' : 'have'} been removed from the waitlist`, "success");
        } catch (error) {
            console.error("Error removing from waitlist:", error);
            showNotification(error.response?.data?.error || error.message || "Failed to remove from waitlist", "error");
        } finally {
            setIsRemovingFromWaitlist(false);
        }
    };

    const handleUserUpdated = async (userId) => {
        try {
            // Lae kasutaja andmed uuesti
            const userData = await getMemberInfo(userId, affiliateId);
            setSelectedUser(userData);
            // Modal jääb avatuks, ainult andmed uuenevad
        } catch (error) {
            console.error("❌ Error updating user profile:", error);
            showNotification("Failed to update user data", "error");
        }
    };

    const handleCheckIn = async (attendee) => {
        try {
            const userId = attendee.userId;
            const familyMemberId = attendee.isFamilyMember ?
                (attendee.familyMemberId || attendee.familyMember?.id) : null;

            await checkInAttendee(cls.id, userId, familyMemberId);

            // MUUDETUD: Kasuta täpsemat võrdlust
            setAttendees(prev =>
                prev.map(a => {
                    // Võrdle nii userId kui ka familyMemberId
                    const aFamilyMemberId = a.isFamilyMember ?
                        (a.familyMemberId || a.familyMember?.id) : null;

                    if (a.userId === userId && aFamilyMemberId === familyMemberId) {
                        return {...a, checkIn: !a.checkIn};
                    }
                    return a;
                })
            );
        } catch (error) {
            console.error("Error checking in attendee:", error);
            showNotification("Failed to check in attendee", "error");
        }
    };

    const handleDelete = async (userId, familyMemberId = null) => {
        setIsDeletingAttendee(true);
        try {
            await deleteAttendee(cls.id, cls.freeClass, userId, familyMemberId);
            setAttendees(prev => prev.filter(a => a.userId !== userId));
            await refreshClasses();

            await fetchAttendees();

            // Also check waitlist after deletion - someone might have been auto-registered
            if (userRole === "affiliate" || userRole === "trainer") {
                await fetchWaitlistEntries();
            }
        } catch (error) {
            console.error("Error deleting attendee:", error);
            showNotification("Failed to delete attendee", "error");
        } finally {
            setIsDeletingAttendee(false);
        }
    };

    const handleOpenProfile = async (userId) => {
        try {
            const userData = await getMemberInfo(userId, affiliateId);
            setSelectedUser(userData);
            setProfileOpen(true);
        } catch (error) {
            console.error("❌ Error fetching user profile:", error);
        }
    };

    // Helper function to check if user or any family member is in waitlist
    const isAnyoneInWaitlist = () => {
        return waitlistStatus.userInWaitlist || waitlistStatus.familyMemberWaitlists.length > 0;
    };

    // Helper function to check if specific person is in waitlist
    const isPersonInWaitlist = (bookFor) => {
        if (bookFor === "self") {
            return waitlistStatus.userInWaitlist;
        } else {
            return waitlistStatus.familyMemberWaitlists.some(
                entry => entry.familyMemberId === parseInt(bookFor)
            );
        }
    };

    // Add confirmation dialog functions
    const handleOpenFamilyCancelConfirm = (familyMemberEnrollment) => {
        setSelectedFamilyMemberToCancel(familyMemberEnrollment);

        setFamilyCancelConfirmOpen(true);
    };

    const handleCloseFamilyCancelConfirm = () => {
        setFamilyCancelConfirmOpen(false);
        setSelectedFamilyMemberToCancel(null);
    };

    const handleOpenTerms = () => {
        setTermsModalOpen(true);
    };

    const handleCloseTerms = () => {
        setTermsModalOpen(false);
    };

    const handleOpenCancelConfirm = () => {
        setCancelConfirmOpen(true);
    };

    const handleCloseCancelConfirm = () => {
        setCancelConfirmOpen(false);
    };

    const handleOpenWaitlistRemoveConfirm = () => {
        setWaitlistRemoveConfirmOpen(true);
    };

    const handleCloseWaitlistRemoveConfirm = () => {
        setWaitlistRemoveConfirmOpen(false);
    };

    const handleOpenDeleteAttendeeConfirm = (attendee) => {
        setAttendeeToDelete(attendee);
        setDeleteAttendeeConfirmOpen(true);
    };

    const handleCloseDeleteAttendeeConfirm = () => {
        setDeleteAttendeeConfirmOpen(false);
        setAttendeeToDelete(null);
    };

    const handleOpenDeleteClassConfirm = () => {
        setDeleteClassConfirmOpen(true);
    };

    const handleCloseDeleteClassConfirm = () => {
        setDeleteClassConfirmOpen(false);
    };

    // Handle change of "Book For" selection
    const handleBookForChange = (event) => {
        setSelectedBookFor(event.target.value);
    };

    const clsForWorkout = useMemo(() => {
        if (!cls) return cls;
        const row = cls.competitionInfo && String(cls.competitionInfo).trim();
        if (row) return cls;
        if (competeDayFallbackContent && String(competeDayFallbackContent).trim()) {
            return { ...cls, competitionInfo: competeDayFallbackContent };
        }
        return cls;
    }, [cls, competeDayFallbackContent]);

    const hasCompetitionInfoTrimmed = !!(clsForWorkout?.competitionInfo && String(clsForWorkout.competitionInfo).trim());
    const hasClassVideos = Array.isArray(cls?.videoLinks)
        ? cls.videoLinks.some((link) => typeof link === "string" && link.trim())
        : typeof cls?.videoLinks === "string" && cls.videoLinks.trim() && cls.videoLinks.trim() !== "[]";
    const showWorkoutInfoSection =
        cls?.wodName
        || cls?.wodType
        || cls?.description
        || cls?.descriptionIntermediate
        || cls?.descriptionBeginner
        || cls?.descriptionStretching
        || cls?.descriptionAtHome
        || hasClassVideos
        || classWorkoutImage?.imageData
        || publicWorkYourWeaknessContent
        || hasCompetitionInfoTrimmed;
    const canRegularUserSeeWorkoutInfo =
        workoutInfoVisibilityMode === "registered"
            ? (isRegistered || familyMemberEnrollments.length > 0)
            : workoutInfoVisibilityMode === "plan"
                ? compatiblePlans.length > 0
                : true;
    const showWorkoutInfoSectionForCurrentUser =
        showWorkoutInfoSection && (userRole !== "regular" || canRegularUserSeeWorkoutInfo);

    if (!cls) return null;

    const classMessageInitialBody = `<p><strong>Class:</strong> ${cls.trainingName || "-"}</p><p><strong>Date:</strong> ${formatClassDate(cls.time, "et-EE", true)}</p><p><strong>Time:</strong> ${formatClassTimeOnly(cls.time, "en-GB")}</p><p><br></p>`;

    function getFlagColor(flag) {
        switch (flag) {
            case "red":
                return "#ff0000";
            case "yellow":
                return "gold";
            default:
                return "green"; // või nt "#999"
        }
    }

    const handleSendMessage = () => {
        setSendMessageOpen(true);
    };

    // Add 1 hour buffer - class is considered "over" for registration purposes 1 hour before start
    // Use utility function from helpers
    const isClassOver = () => checkClassOver(cls?.time, cancelBeforeHours);

    // WorkoutInfoFullScreen component is now in separate file

    const handleOpenAddAttendeeModal = () => {
        setAddAttendeeModalOpen(true);
    };

    const handleCloseAddAttendeeModal = () => {
        setAddAttendeeModalOpen(false);
    };

    const handleAttendeeAdded = async () => {
        // Refresh the attendees list and class data
        await fetchAttendees();
        await refreshClasses();
        showNotification("Attendee added successfully!", "success");
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionProps={{onExited}}
            maxWidth="md"
            fullWidth
            fullScreen={window.innerWidth < 600}
        >
            <DialogTitle
                sx={{textAlign: "center", fontSize: "1.8rem", fontWeight: "bold"}}
            >
                {cls.trainingName}
            </DialogTitle>

            <DialogContent sx={{p: 1}}>
                {isLoadingClassData && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            zIndex: 999
                        }}
                    >
                        <CircularProgress size={60}/>
                    </Box>
                )}
                {/* Põhiinfo sektsioon */}
                <Grid container spacing={3} sx={{paddingY: 2}}>
                    {/* Vasak veerg */}
                    <Grid item xs={12} md={6}>
                        <ClassDetails
                            cls={cls}
                            userRole={userRole}
                            isClassFull={isClassFull}
                            isRegistered={isRegistered}
                            isInWaitlist={isInWaitlist}
                            waitlistCount={waitlistCount}
                            attendees={attendees}
                            guestRegistrations={guestRegistrations}
                            selectedAffiliateListVisible={selectedAffiliateListVisible}
                            isAllClassListVisible={isAllClassListVisible}
                            hasAnyPlans={canSeeParticipantList}
                            hideTrainer={hideTrainer}
                        />
                    </Grid>

                    {/* Parem veerg - peidetud mobiilis */}
                    {showWorkoutInfoSectionForCurrentUser ? (
                        <Grid item xs={12} md={6} sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <WorkoutInfo
                                cls={clsForWorkout}
                                workYourWeaknessContent={publicWorkYourWeaknessContent}
                                toggleWorkoutInfoFullScreen={toggleWorkoutInfoFullScreen}
                                closeWorkoutInfoFullScreen={closeWorkoutInfoFullScreen}
                                handleAddClassToMyTrainings={handleAddClassToMyTrainings}
                                userRole={userRole}
                                showStretchingAtHomeWyw={userRole !== "regular" || hasCompatiblePlans}
                                hasCompatiblePlans={hasCompatiblePlans}
                                workoutImage={classWorkoutImage}
                            />
                        </Grid>
                    ) : (
                        ""
                    )}
                </Grid>

                {canShowSmartLockForRole && smartLockAffiliateId && (
                    <Box sx={{ mt: 1, mb: 1, width: "100%" }}>
                        <SmartLockUnlockButton
                            affiliateId={smartLockAffiliateId}
                            classId={cls?.id}
                            disabled={isLoadingClassData}
                            fullWidth
                            eligibleOnly
                        />
                    </Box>
                )}

                <Divider sx={{marginY: 2}}/>

                {/* Loading indicator for regular users */}
                {userRole === "regular" && isLoadingUserData && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {userRole === "regular" && !isLoadingUserData && (
                    <Box mb={2}>
                        {/* Show current registrations */}
                        {(isRegistered || familyMemberEnrollments.length > 0) && (
                            <Box mb={2}>
                                <Typography variant="h6" sx={{fontWeight: "bold", mb: 1}}>
                                    Current Registrations:
                                </Typography>
                                {isRegistered && (
                                    <Box sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 1,
                                        p: 1,
                                        backgroundColor: "#e8f5e8",
                                        borderRadius: 1
                                    }}>
                                        <Typography>
                                            ✅ You are registered for this class
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            size="small"
                                            disabled={isClassOver() || isCancelling}
                                            onClick={handleOpenCancelConfirm}
                                        >
                                            {isCancelling ? <CircularProgress size={16} color="inherit"/> : "Cancel"}
                                        </Button>
                                    </Box>
                                )}
                                {familyMemberEnrollments.map((enrollment) => (
                                    <Box
                                        key={enrollment.id}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: 1,
                                            p: 1,
                                            backgroundColor: "#e3f2fd",
                                            borderRadius: 1
                                        }}
                                    >
                                        <Typography>
                                            ✅ {enrollment.familyMemberName} is registered for this class
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            size="small"
                                            disabled={isClassOver()}
                                            onClick={() => handleOpenFamilyCancelConfirm(enrollment)}
                                        >
                                            Cancel
                                        </Button>
                                    </Box>
                                ))}
                                <Divider sx={{my: 2}}/>
                            </Box>
                        )}

                        {/* Show current waitlist entries */}
                        {(waitlistStatus.userInWaitlist || waitlistStatus.familyMemberWaitlists.length > 0) && (
                            <Box mb={2}>
                                <Typography variant="h6" sx={{fontWeight: "bold", mb: 1}}>
                                    Waitlist Entries:
                                </Typography>
                                {waitlistStatus.userInWaitlist && (
                                    <Box sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 1,
                                        p: 1,
                                        backgroundColor: "#fff3e0",
                                        borderRadius: 1
                                    }}>
                                        <Typography>
                                            ⏳ You are on the waitlist for this class
                                            {userWaitlistPosition && (
                                                <span style={{fontWeight: 'bold', color: '#ff6f00'}}>
                    {' '}(Position: {userWaitlistPosition})
                </span>
                                            )}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            color="warning"
                                            size="small"
                                            disabled={isRemovingFromWaitlist}
                                            onClick={() => handleRemoveFromWaitlist("self")}
                                        >
                                            {isRemovingFromWaitlist ?
                                                <CircularProgress size={16} color="inherit"/> : "Remove"}
                                        </Button>
                                    </Box>
                                )}
                                {waitlistStatus.familyMemberWaitlists.map((waitlistEntry) => (
                                    <Box
                                        key={waitlistEntry.id}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: 1,
                                            p: 1,
                                            backgroundColor: "#fff3e0",
                                            borderRadius: 1
                                        }}
                                    >
                                        <Typography>
                                            ⏳ {waitlistEntry.familyMemberName} is on the waitlist for this class
                                            {familyWaitlistPositions[waitlistEntry.familyMemberId] && (
                                                <span style={{fontWeight: 'bold', color: '#ff6f00'}}>
                    {' '}(Position: {familyWaitlistPositions[waitlistEntry.familyMemberId]})
                </span>
                                            )}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            color="warning"
                                            size="small"
                                            disabled={isRemovingFromWaitlist}
                                            onClick={() => handleRemoveFromWaitlist("family", waitlistEntry.familyMemberId)}
                                        >
                                            {isRemovingFromWaitlist ?
                                                <CircularProgress size={16} color="inherit"/> : "Remove"}
                                        </Button>
                                    </Box>
                                ))}
                                <Divider sx={{my: 2}}/>
                            </Box>
                        )}

                        {/* Show registration form if user can still register more people */}
                        {(!isRegistered || familyMemberEnrollments.length < familyMembers.length) && (
                            <>
                                {isClassFull ? (
                                    // Waitlist registration
                                    <>
                                        <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                                                {familyMembers.length > 0 && (
                                                    <FormControl sx={{minWidth: 200}}>
                                                        <InputLabel id="book-for-select-label">Book For</InputLabel>
                                                        <Select
                                                            labelId="book-for-select-label"
                                                            value={selectedBookFor}
                                                            label="Book For"
                                                            onChange={handleBookForChange}
                                                        >
                                                            {!isRegistered && (
                                                                <MenuItem
                                                                    value="self">{userProfile?.fullName || "Yourself"}</MenuItem>
                                                            )}
                                                            {familyMembers.map((member) => {
                                                                const isAlreadyRegistered = familyMemberEnrollments.some(
                                                                    enrollment => enrollment.familyMemberId === member.id
                                                                );
                                                                return !isAlreadyRegistered ? (
                                                                    <MenuItem key={member.id}
                                                                              value={member.id.toString()}>
                                                                        {member.fullName}
                                                                    </MenuItem>
                                                                ) : null;
                                                            })}
                                                        </Select>
                                                    </FormControl>
                                                )}

                                                {hasCompatiblePlans ? (
                                                    <Box display="flex" alignItems="center" gap={2}>
                                                        <FormControl sx={{minWidth: 200}}>
                                                            <InputLabel id="plan-select-label">Select Plan</InputLabel>
                                                            <Select
                                                                labelId="plan-select-label"
                                                                value={selectedPlanId || ""}
                                                                label="Select Plan"
                                                                onChange={(e) => setSelectedPlanId(e.target.value)}
                                                            >
                                                                {filteredPlans.map((plan) => (
                                                                    <MenuItem key={plan.id} value={plan.id}>
                                                                        {plan.planName} (Sessions
                                                                        left: {plan.sessionsLeft})
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            disabled={isClassTimePassed() || !isWithinPeriod || isAddingToWaitlist}
                                                            onClick={handleAddToWaitlist}
                                                        >
                                                            {isAddingToWaitlist ? <CircularProgress size={20} color="inherit"/> : "Waitlist"}
                                                        </Button>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                                                        <Typography color="error">
                                                            {hasAnyPlans 
                                                                ? "No compatible plans found for the selected person." 
                                                                : "You have no valid plans."}
                                                        </Typography>
                                                        {!isClassTimePassed() && (
                                                            <Button
                                                                variant="contained"
                                                                color="success"
                                                                onClick={handleBuyPlans}
                                                                sx={{width: {xs: "100%", sm: "auto"}}}
                                                            >
                                                                Buy Plans
                                                            </Button>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                    </>
                                ) : (
                                    // Regular registration
                                    // Only show if user is not registered OR if there are unregistered family members
                                    <>
                                        {userRole === "regular" && (!isRegistered || (familyMembers.length > 0 && familyMembers.some(member => 
                                            !familyMemberEnrollments.some(enrollment => enrollment.familyMemberId === member.id)
                                        ))) && (
                                            <Box sx={{mt: 2, mb: 2}}>
                                                {!termsAccepted && (cls.freeClass || isFirstTraining || hasCompatiblePlans) && (
                                                    <Box>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={termsCheck}
                                                                    onChange={(e) => setTermsCheck(e.target.checked)}
                                                                    disabled={isAcceptingTerms}
                                                                />
                                                            }
                                                            label={
                                                                <Typography variant="body2">
                                                                    I have read and agree to the {' '}
                                                                    <Link
                                                                        component="button"
                                                                        variant="body2"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            handleOpenTerms();
                                                                        }}
                                                                    >
                                                                        Terms and Conditions
                                                                    </Link>
                                                                </Typography>
                                                            }
                                                        />
                                                        {isAcceptingTerms && (
                                                            <CircularProgress size={20} sx={{ml: 2}}/>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        )}

                                        {cls.freeClass || isFirstTraining ? (
                                            <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                                                {isFirstTraining && (
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{mb: 0.5}}>
                                                            Additional Information (Optional)
                                                        </Typography>
                                                        <Typography variant="caption" sx={{mb: 1, color: 'text.secondary', display: 'block'}}>
                                                            This information will be sent to the club
                                                        </Typography>
                                                        <Box
                                                            component="textarea"
                                                            value={additionalInfo}
                                                            onChange={(e) => setAdditionalInfo(e.target.value)}
                                                            placeholder="Please share any previous sports background, injuries, or information you'd like to share with the club..."
                                                            sx={{
                                                                width: '100%',
                                                                minHeight: '60px',
                                                                padding: '10px',
                                                                border: '1px solid',
                                                                borderColor: 'rgba(0, 0, 0, 0.23)',
                                                                borderRadius: '4px',
                                                                fontSize: '14px',
                                                                fontFamily: 'inherit',
                                                                resize: 'vertical',
                                                                '&:focus': {
                                                                    outline: 'none',
                                                                    borderColor: 'primary.main',
                                                                    borderWidth: '2px'
                                                                },
                                                                '&:hover': {
                                                                    borderColor: 'rgba(0, 0, 0, 0.87)'
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    disabled={isClassTimePassed() || (!termsCheck && !termsAccepted) || !isWithinPeriod || !cls.canRegister || isRegistering}
                                                    onClick={handleRegister}
                                                >
                                                    {isRegistering ?
                                                        <CircularProgress size={20} color="inherit"/> : "Register"}
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                                                {familyMembers.length > 0 && (
                                                    <FormControl sx={{minWidth: 200}}>
                                                        <InputLabel id="book-for-select-label">Book For</InputLabel>
                                                        <Select
                                                            labelId="book-for-select-label"
                                                            value={selectedBookFor}
                                                            label="Book For"
                                                            onChange={handleBookForChange}
                                                        >
                                                            {!isRegistered && (
                                                                <MenuItem
                                                                    value="self">{userProfile?.fullName || "Yourself"}</MenuItem>
                                                            )}
                                                            {familyMembers.map((member) => {
                                                                const isAlreadyRegistered = familyMemberEnrollments.some(
                                                                    enrollment => enrollment.familyMemberId === member.id
                                                                );
                                                                return !isAlreadyRegistered ? (
                                                                    <MenuItem key={member.id}
                                                                              value={member.id.toString()}>
                                                                        {member.fullName}
                                                                    </MenuItem>
                                                                ) : null;
                                                            })}
                                                        </Select>
                                                    </FormControl>
                                                )}

                                                {hasCompatiblePlans ? (
                                                    <Box
                                                        sx={{
                                                            display: {xs: "flex", sm: "flex"},
                                                            flexDirection: {xs: "column", sm: "row"},
                                                            alignItems: {xs: "stretch", sm: "center"},
                                                            gap: 2
                                                        }}
                                                    >
                                                        <FormControl
                                                            sx={{
                                                                minWidth: 200,
                                                                width: {xs: "100%", sm: "auto"}
                                                            }}
                                                        >
                                                            <InputLabel id="plan-select-label">Select Plan</InputLabel>
                                                            <Select
                                                                labelId="plan-select-label"
                                                                value={selectedPlanId || ""}
                                                                label="Select Plan"
                                                                onChange={(e) => setSelectedPlanId(e.target.value)}
                                                            >
                                                                {filteredPlans.map((plan) => (
                                                                    <MenuItem key={plan.id} value={plan.id}>
                                                                        {plan.planName} (Sessions
                                                                        left: {plan.sessionsLeft})
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                        <Button
                                                            variant="contained"
                                                            color="success"
                                                            disabled={isClassTimePassed() || (!termsCheck && !termsAccepted) || !isWithinPeriod || !cls.canRegister || isRegistering}
                                                            onClick={handleRegister}
                                                        >
                                                            {isRegistering ? <CircularProgress size={20}
                                                                                               color="inherit"/> : "Register"}
                                                        </Button>
                                                    </Box>
                                                ) : hasAnyPlans ? (
                                                    <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                                                        <Typography color="error">
                                                            No compatible plans found for the selected person.
                                                        </Typography>
                                                        {!isClassTimePassed() && (
                                                            <Button
                                                                variant="contained"
                                                                color="success"
                                                                onClick={handleBuyPlans}
                                                                sx={{width: {xs: "100%", sm: "auto"}}}
                                                            >
                                                                Buy Plans
                                                            </Button>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                                                        <Typography color="error">
                                                            You have no valid plans.
                                                        </Typography>
                                                        {!isClassTimePassed() && (
                                                            <Button
                                                                variant="contained"
                                                                color="success"
                                                                onClick={handleBuyPlans}
                                                                sx={{width: {xs: "100%", sm: "auto"}}}
                                                            >
                                                                Buy Plans
                                                            </Button>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Box>
                )}
                {userRole === "regular" && (
                    <Box mb={2}>
                        {!showScoreForm && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setShowScoreForm(true)}
                                sx={{mr: 2, width: {xs: "100%", sm: "auto"}}}
                            >
                                {hasScore ? "Edit Score" : "Add Score"}
                            </Button>
                        )}

                        {/* Score form */}
                        {showScoreForm && (
                            <Box
                                sx={{
                                    mb: 2,
                                    border: "1px solid #ccc",
                                    borderRadius: "8px",
                                    p: 2,
                                }}
                            >
                                <Typography variant="h6" sx={{mb: 2}}>
                                    {hasScore ? "Edit Your Score" : "Add Your Score"}
                                </Typography>

                                {/* RADIO GROUP scoreType */}
                                <RadioGroup
                                    row
                                    value={scoreType}
                                    onChange={(e) => setScoreType(e.target.value)}
                                    sx={{mb: 2}}
                                >
                                    <FormControlLabel value="rx" control={<Radio/>} label="RX"/>
                                    <FormControlLabel value="sc" control={<Radio/>} label="Intermediate"/>
                                    <FormControlLabel value="beg" control={<Radio/>} label="Beginner"/>
                                </RadioGroup>

                                <Typography variant="caption">
                                    Reps/Time/Weight. For example: 147, 5:30, 50. Your name and score will be visible to
                                    others.
                                </Typography>

                                {/* Score field */}
                                <TextField
                                    label="Your Score"
                                    variant="outlined"
                                    value={scoreValue}
                                    onChange={(e) => setScoreValue(e.target.value)}
                                    fullWidth
                                    sx={{mb: 2, mt: 1}}
                                />
                                <TextField
                                    label="Comment"
                                    variant="outlined"
                                    value={commentValue}
                                    onChange={(e) => setCommentValue(e.target.value)}
                                    fullWidth
                                    sx={{mb: 2, mt: 1}}
                                />

                                {/* SAVE + CANCEL buttons */}
                                <Box sx={{display: "flex", justifyContent: "flex-end"}}>


                                    <Button
                                        variant="outlined"

                                        onClick={() => setShowScoreForm(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSaveScore}
                                        sx={{ml: 2}}
                                    >
                                        Save
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
                {/* Osalejate sektsioon */}
                {(canManageClass || isTemporaryTrainer || (selectedAffiliateListVisible === true && isRegistered) || (isAllClassListVisible === true && canSeeParticipantList === true)) && (
                    <>
                        <ParticipantsList
                            userRole={userRole}
                            currentUserId={currentUserId}
                            attendees={attendees}
                            handleCheckInAll={handleCheckInAll}
                            allCheckedIn={allCheckedIn}
                            isCheckingInAll={isCheckingInAll}
                            handleSendMessage={handleSendMessage}
                            handleCheckIn={handleCheckIn}
                            handleOpenDeleteAttendeeConfirm={handleOpenDeleteAttendeeConfirm}
                            handleOpenProfile={handleOpenProfile}
                            getFlagColor={getFlagColor}
                            setAddAttendeeModalOpen={setAddAttendeeModalOpen}
                            viewOnly={isTemporaryTrainer}
                            isClassListVisible={selectedAffiliateListVisible === true}
                            isAllClassListVisible={isAllClassListVisible === true}
                            currentUserClassListNameVisible={userProfile?.isClassListNameVisible === true}
                        />

                        {canManageClass && waitlistEntries.length > 0 && (
                            <WaitlistSection
                                userRole={userRole}
                                waitlistEntries={waitlistEntries}
                                handlePromoteFromWaitlist={handlePromoteFromWaitlist}
                                handleOpenProfile={handleOpenProfile}
                                isPromotingFromWaitlist={isPromotingFromWaitlist}
                            />
                        )}

                        {/* Guest Registrations Section - AINULT affiliate ja trainer jaoks */}
                        {canManageClass && (
                            <GuestRegistrationsList
                                guestRegistrations={guestRegistrations}
                                onMessageGuests={() => setSendMessageOpen(true)}
                                onAddGuest={handleAddGuest}
                                onDeleteGuest={handleDeleteGuest}
                                classId={cls.id}
                                affiliateId={affiliateId}
                                classData={cls}
                                subdomain={affiliateSubdomain}
                                userRole={userRole}
                            />
                        )}
                    </>
                )}


                {showWorkoutInfoSectionForCurrentUser && (
                    <Box sx={{ display: { xs: "block", sm: "none" }, mb: 2 }}>
                        <WorkoutInfo
                            cls={clsForWorkout}
                            workYourWeaknessContent={publicWorkYourWeaknessContent}
                            toggleWorkoutInfoFullScreen={toggleWorkoutInfoFullScreen}
                            closeWorkoutInfoFullScreen={closeWorkoutInfoFullScreen}
                            handleAddClassToMyTrainings={handleAddClassToMyTrainings}
                            userRole={userRole}
                            showStretchingAtHomeWyw={userRole !== "regular" || hasCompatiblePlans}
                            hasCompatiblePlans={hasCompatiblePlans}
                            workoutImage={classWorkoutImage}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{justifyContent: "space-between", padding: "16px"}}>

                <IconButton
                    color="primary"
                    onClick={() => setLeaderboardOpen(true)}
                    sx={{
                        ml: 1,
                        color: "goldenrod",
                        "&:hover": {
                            backgroundColor: "rgba(218,165,32,0.1)"
                        }
                    }}
                >
                    <EmojiEventsIcon/>
                </IconButton>
                {/* Delete button - for both affiliate and trainer */}
                {(userRole === 'affiliate' || userRole === 'trainer') && (
                    <IconButton
                        onClick={handleOpenDeleteClassConfirm}
                        aria-label="delete"
                        sx={{
                            color: 'white',
                            bgcolor: '#d32f2f',
                            '&:hover': {bgcolor: '#b71c1c'}
                        }}
                    >
                        <DeleteIcon/>
                    </IconButton>
                )}

                {/* Edit button - for both affiliate and trainer */}
                {(userRole === 'affiliate' || userRole === 'trainer') && (
                    <IconButton
                        color="primary"
                        onClick={() => onEdit(cls)}
                        aria-label="edit"
                    >
                        <EditIcon/>
                    </IconButton>
                )}
                {(userRole === 'affiliate' || userRole === 'trainer') && (
                    <IconButton
                        color="primary"
                        onClick={() => setCancellationHistoryOpen(true)}
                        aria-label="cancellation-history"
                        title="Cancellation history"
                    >
                        <HistoryIcon/>
                    </IconButton>
                )}

                <Button onClick={onClose} color="inherit" variant="contained">
                    Close
                </Button>
            </DialogActions>

            {/* Full-screen workout info component */}
            <WorkoutInfoFullScreen
                open={isWorkoutInfoFullScreen}
                onClose={toggleWorkoutInfoFullScreen}
                cls={clsForWorkout}
                isLoading={isLoadingClassData}
                workoutImage={classWorkoutImage}
            />

            <LeaderboardModal
                open={isLeaderboardOpen}
                onClose={() => setLeaderboardOpen(false)}
                classId={cls.id}
                cls={cls}
                hasScore={hasScore}
                userProfile={userProfile}
            />
            <ProfileModal
                open={isProfileOpen}
                onClose={() => setProfileOpen(false)}
                user={selectedUser}
                affiliateId={affiliateId}
                affiliateEmail={affiliateEmail}
                onUserUpdated={handleUserUpdated}
            />
            <SendMessageModal
                open={isSendMessageOpen}
                onClose={() => setSendMessageOpen(false)}
                affiliate={affiliateId}
                affiliateEmail={affiliateEmail}
                initialBody={classMessageInitialBody}
                preSelectedUsers={Array.isArray(attendees) ? attendees.map(attendee => ({
                    id: attendee.userId,
                    fullName: attendee.fullName
                })) : []}
                preSelectedGuests={Array.isArray(guestRegistrations) ? guestRegistrations.map(guest => ({
                    id: guest.id,
                    firstName: guest.firstName,
                    lastName: guest.lastName,
                    email: guest.email
                })) : []}
            />

            <AddAttendeeModal
                open={isAddAttendeeModalOpen}
                onClose={handleCloseAddAttendeeModal}
                classId={cls?.id}
                affiliateId={affiliateId}
                onSuccess={handleAttendeeAdded}
                fetchAttendees={fetchAttendees}
            />

            <AffiliateTermsModal
                open={termsModalOpen}
                onClose={handleCloseTerms}
                affiliateId={cls?.affiliateId}
            />

            {/* Cancel Registration Confirmation Dialog */}
            <Dialog
                open={cancelConfirmOpen}
                onClose={handleCloseCancelConfirm}
            >
                <DialogTitle>Confirm Cancellation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to cancel your registration for this class? You may lose your spot if the
                        class is in high demand.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCancelConfirm} variant="contained"
                            color="primary">
                        No
                    </Button>
                    <Button color="inherit" variant="outlined" onClick={() => {
                        handleCancelRegistration();
                        handleCloseCancelConfirm();
                    }}>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Remove from Waitlist Confirmation Dialog */}
            <Dialog
                open={waitlistRemoveConfirmOpen}
                onClose={handleCloseWaitlistRemoveConfirm}
            >
                <DialogTitle>Confirm Removal</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove yourself from the waitlist? You'll lose your current position if
                        you decide to join again later.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseWaitlistRemoveConfirm} color="primary">
                        No, Stay on Waitlist
                    </Button>
                    <Button onClick={() => {
                        handleRemoveFromWaitlist();
                        handleCloseWaitlistRemoveConfirm();
                    }} color="warning">
                        Yes, Remove Me
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Attendee Confirmation Dialog */}
            <Dialog
                open={deleteAttendeeConfirmOpen}
                onClose={handleCloseDeleteAttendeeConfirm}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove this attendee from the class? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCloseDeleteAttendeeConfirm} 
                        color="primary"
                        disabled={isDeletingAttendee}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={async () => {
                            // Leia familyMemberId attendee objektist
                            const userId = attendeeToDelete?.userId;
                            const familyMemberId = attendeeToDelete?.isFamilyMember ?
                                (attendeeToDelete?.familyMemberId || attendeeToDelete?.familyMember?.id) : null;

                            console.log('Deleting attendee:', {
                                userId,
                                familyMemberId,
                                isFamilyMember: attendeeToDelete?.isFamilyMember
                            });

                            await handleDelete(userId, familyMemberId);
                            handleCloseDeleteAttendeeConfirm();
                        }} 
                        color="error"
                        disabled={isDeletingAttendee}
                        startIcon={isDeletingAttendee ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        Remove Attendee
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Notification Dialog - zIndex above ClassModal so it stays visible */}
            <Dialog
                open={notificationOpen}
                onClose={() => setNotificationOpen(false)}
            >
                <DialogTitle>
                    {notificationType === "success" && "Success"}
                    {notificationType === "error" && "Error"}
                    {notificationType === "info" && "Information"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ whiteSpace: "pre-line" }}>
                        {notificationMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNotificationOpen(false)} variant="contained"
                            color="primary" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Class Confirmation Dialog */}
            <Dialog
                open={deleteClassConfirmOpen}
                onClose={isDeletingClass ? undefined : handleCloseDeleteClassConfirm}
            >
                <DialogTitle>Confirm Class Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this class? This will remove all registrations and cannot be
                        undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCloseDeleteClassConfirm} 
                        color="primary"
                        disabled={isDeletingClass}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={async () => {
                            setIsDeletingClass(true);
                            try {
                                await onDelete(cls.id);
                                handleCloseDeleteClassConfirm();
                            } catch (error) {
                                console.error("Error deleting class:", error);
                                showNotification("Failed to delete class", "error");
                            } finally {
                                setIsDeletingClass(false);
                            }
                        }} 
                        color="error"
                        disabled={isDeletingClass}
                        startIcon={isDeletingClass ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isDeletingClass ? "Deleting..." : "Delete Class"}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Family Member Cancel Registration Confirmation Dialog */}
            <Dialog
                open={familyCancelConfirmOpen}
                onClose={handleCloseFamilyCancelConfirm}
            >
                <DialogTitle>Confirm Cancellation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to cancel {selectedFamilyMemberToCancel?.familyMemberName}'s registration
                        for this class?
                        They may lose their spot if the class is in high demand.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseFamilyCancelConfirm} variant="contained" color="primary">
                        No
                    </Button>
                    <Button
                        color="inherit"
                        variant="outlined"
                        disabled={isCancelling}
                        onClick={async () => {
                            await handleCancelFamilyMemberRegistration(selectedFamilyMemberToCancel?.id);
                            handleCloseFamilyCancelConfirm();
                        }}
                    >
                        {isCancelling ? <CircularProgress size={16} color="inherit"/> : "Yes"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={cancellationHistoryOpen}
                onClose={() => setCancellationHistoryOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Cancellation History</DialogTitle>
                <DialogContent>
                    {isLoadingCancellationHistory ? (
                        <Box sx={{display: "flex", justifyContent: "center", py: 2}}>
                            <CircularProgress size={24}/>
                        </Box>
                    ) : cancellationHistoryError ? (
                        <DialogContentText>{cancellationHistoryError}</DialogContentText>
                    ) : cancellationHistory.length === 0 ? (
                        <DialogContentText>No cancellations recorded for this class.</DialogContentText>
                    ) : (
                        <List>
                            {cancellationHistory.map((entry) => {
                                const cancelledPerson = entry.cancelledFamilyMember?.fullName
                                    || entry.cancelledUser?.fullName
                                    || entry.cancelledUser?.email
                                    || "Unknown";
                                const cancelledBy = entry.cancelledByUser?.fullName
                                    || entry.cancelledByUser?.email
                                    || "Unknown";

                                return (
                                    <ListItem key={entry.id} disablePadding sx={{py: 1}}>
                                        <ListItemText
                                            primary={`${cancelledPerson} cancelled registration`}
                                            secondary={`Cancelled by: ${cancelledBy} | ${new Date(entry.cancelledAt).toLocaleString()}`}
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancellationHistoryOpen(false)} variant="contained" color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>

    );
}